import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { PageHeader } from 'components/PageHeader';
import { PageListDraggable } from 'components/PageListDraggable';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { theme } from 'lib/theme';
import { isEqual } from 'lodash';
import React from 'react';
import {
  DragEndParams,
  RenderItemParams,
} from 'react-native-draggable-flatlist';
import { useDispatch } from 'react-redux';
import {
  eventReorderScreenClose,
  eventReorderScreenDone,
  NAVIGATE_MODAL_BACK,
} from 'store/actions';

export const ReorderScreen: React.FC<
  ModalScreenProps<ModalScreens.REORDER>
> = ({ route, navigation }) => {
  const dispatch = useDispatch();
  const confirmUnsaved = useConfirmUnsaved();

  const {
    options,
    title,
    subtitle,
    action,
    eventKey,
    closeOnDone = true,
  } = route.params;

  const initOrder = React.useRef(options.map((o) => o.value));
  const [order, setOrder] = React.useState(initOrder.current);

  const checkIsDirty = () => !isEqual(initOrder.current, order);

  const renderItem = React.useCallback(
    ({ drag, item, isActive }: RenderItemParams<(typeof order)[number]>) => {
      const option = options.find((o) => o.value === item)!;
      return (
        <Card>
          <CardRow
            label={option.name}
            labelStyle={option.valueStyle}
            key={option.value}
            icon={option.icon}
            iconColor={option.iconColor}
            onDrag={drag}
            style={
              isActive ? { backgroundColor: theme.colors.blue10 } : undefined
            }
          />
        </Card>
      );
    },
    [options],
  );

  const handleDragEnd = React.useCallback(
    ({ data: newOrder }: DragEndParams<(typeof order)[number]>) =>
      setOrder(newOrder),
    [],
  );

  const handleClose = () => {
    if (checkIsDirty()) {
      confirmUnsaved({
        onSave: handleSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  };

  const handleSave = () => {
    if (action) {
      dispatch({
        ...action,
        // @ts-expect-error payload does exist
        payload: { ...(action.payload || {}), order },
      });
      // The action should dispatch NAVIGATE_MODAL_BACK itself
      // This allows for async with failures
      return;
    }
    if (eventKey) {
      dispatch(
        eventReorderScreenDone({
          key: eventKey,
          numberValues: order.filter((v) => typeof v === 'number') as number[],
          stringValues: order.filter((v) => typeof v === 'string') as string[],
        }),
      );
    }
    if (closeOnDone) {
      dispatch(NAVIGATE_MODAL_BACK());
    }
  };

  const isDirtyVar = checkIsDirty();
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(handleClose);

  React.useEffect(() => {
    return () => {
      if (eventKey) {
        dispatch(eventReorderScreenClose({ key: eventKey }));
      }
    };
  }, [dispatch, eventKey]);

  return (
    <Page
      keyboardAvoiding={false}
      header={
        <PageHeader
          title={title || 'Reorder'}
          subtitle={subtitle}
          onPressCancelButton={handleClose}
          onPressSaveButton={handleSave}
        />
      }
    >
      <PageListDraggable
        data={order}
        renderItem={renderItem}
        keyExtractor={(item) => `${item}`}
        onDragEnd={handleDragEnd}
      ></PageListDraggable>
    </Page>
  );
};
